import React, { useCallback, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import Modal from "../UIElements/Modal";
import { AuthContext } from "../../context/auth-context";
import { useIdleTimer } from "../../hooks/idle-hook";
import { COUNTDOWN_MS, IDLE_MS, TIMEOUT_MS, clock, minutes, setLogoutReason } from "../../util/idle";
import "../FormElements/Button.css";
import "./Session.css";

// Logs the user out after TIMEOUT_MS without activity (shared/util/idle.js):
// "Still there?" with a countdown of the last COUNTDOWN_MS. "Stay logged
// in", Esc or a click next to it keep the session. Rendered inside the
// router while logged in (App.js).
const IdleLogout = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const expire = useCallback(() => {
    setLogoutReason("idle");
    auth.logout();
    navigate("/auth", { replace: true });
  }, [auth, navigate]);

  const { warning, remaining, stay } = useIdleTimer({ active: auth.isLoggedIn, onExpire: expire });
  const left = clock(remaining);

  // The countdown in the tab's title, for a tab in the background
  useEffect(() => {
    if (!warning) return undefined;
    const title = document.title;
    return () => { document.title = title; };
  }, [warning]);
  useEffect(() => {
    if (warning) document.title = `(${left}) Still there? · MTT`;
  }, [warning, left]);

  useEffect(() => {
    if (!warning) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") stay();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [warning, stay]);

  const logoutNow = () => {
    auth.logout();
    navigate("/", { replace: true });
  };

  // read out every 15 seconds, not every second
  const spoken = Math.ceil(remaining / 15000) * 15;

  return (
    <Modal
      show={warning}
      onCancel={stay}
      role="alertdialog"
      labelledBy="idle-logout-title"
      describedBy="idle-logout-text"
      header="Still there?"
      className="session-modal"
      headerClass="session-modal__header"
      contentClass="session-modal__content"
      footerClass="session-modal__footer"
      footer={(
        <>
          <button type="button" className="button" onClick={stay} autoFocus>Stay logged in</button>
          <button type="button" className="button button--inverse" onClick={logoutNow}>Log out now</button>
        </>
      )}
    >
      <p id="idle-logout-text">
        Nothing happened for {minutes(IDLE_MS)}. For your security you'll be logged out in{" "}
        <strong className="session-modal__clock">{left}</strong>.
      </p>
      <div className="session-modal__bar" aria-hidden="true">
        <span style={{ width: `${Math.min(100, (remaining / COUNTDOWN_MS) * 100)}%` }} />
      </div>
      <span className="session-modal__live" aria-live="polite">
        {warning ? `You'll be logged out in about ${spoken} seconds.` : ""}
      </span>
    </Modal>
  );
};

export const IDLE_NOTICE = `You were logged out after ${minutes(TIMEOUT_MS)} without activity.`;

export default IdleLogout;
